import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, Sparkles } from "lucide-react";
import { VIDEO_URL, VIDEO_WEBM_URL, VIDEO_POSTER } from "@/i18n";

export const Hero = ({ t }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const lines = [t.hero.line1, t.hero.line2, t.hero.line3];

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-end overflow-hidden bg-[#0A272C]"
    >
      <motion.div style={{ y: videoY }} className="absolute inset-0 scale-110">
        <video
          data-testid="hero-video"
          className="h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={VIDEO_POSTER}
        >
          <source src={VIDEO_WEBM_URL} type="video/webm" />
          <source src={VIDEO_URL} type="video/mp4" />
        </video>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A272C]/90 via-[#0A272C]/60 to-[#0A272C]/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A272C]/85 via-transparent to-[#0A272C]/40" />

      <motion.div
        style={{ opacity: contentOpacity, y: contentY }}
        className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-8 pt-44 pb-32"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-[#B9D7F0] border border-white/25 rounded-full px-4 py-2 bg-white/10 backdrop-blur-md"
        >
          <Sparkles size={14} />
          {t.hero.label}
        </motion.p>

        <h1 className="mt-8 font-jakarta text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] max-w-3xl">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "text-[#A8DAE0]" : "text-white"}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.3 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-8 max-w-xl text-base sm:text-lg text-slate-200 leading-relaxed"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.05 }}
          className="mt-10 flex flex-col sm:flex-row items-start gap-4"
        >
          <a
            href="#kontakt"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-[#0A272C] font-semibold text-sm transition-all duration-200 hover:bg-[#A8DAE0] shadow-[0_8px_30px_rgba(0,0,0,0.35)]"
          >
            {t.hero.ctaPrimary}
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </a>
          <a
            href="#leistungen"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/40 text-white font-semibold text-sm hover:bg-white/10 hover:border-white transition-all duration-200"
          >
            {t.hero.ctaSecondary}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.25 }}
          className="mt-12 flex flex-wrap items-center gap-3"
        >
          {t.hero.pills.map((pill, i) => (
            <span
              key={i}
              data-testid={`hero-pill-${i}`}
              className="text-xs font-mono uppercase tracking-[0.15em] text-white/85 border border-white/25 rounded-full px-4 py-2 bg-white/5 backdrop-blur-sm"
            >
              {pill}
            </span>
          ))}
        </motion.div>

        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.45 }}
          className="mt-8 inline-flex items-center text-xs font-mono uppercase tracking-[0.2em] text-white bg-[#0A272C]/80 backdrop-blur px-4 py-2 rounded-full"
        >
          {t.hero.videoCaption}
        </motion.span>
      </motion.div>

      <motion.img
        src="/logo-white.png"
        alt=""
        aria-hidden="true"
        data-testid="hero-watermark"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.55 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 right-6 sm:right-10 z-10 w-28 sm:w-36 hidden sm:block"
      />

      <motion.a
        href="#staerken"
        data-testid="hero-scroll-indicator"
        aria-label={t.hero.scroll}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/70 hover:text-white transition-colors"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }} className="block">
          <ArrowDown size={20} />
        </motion.span>
      </motion.a>
    </section>
  );
};
